import { MaturityLevelType, DepartmentSP, Department } from "../types/data";
import { ResponseSummary, ResponseFactory, ResponseActions, ResponseDataMain, ResponseReportFactory } from "../types/response";
import { months } from "../const";
import moment from "moment";
import { filterByRange, findQuestionnaire } from "./utils";
import { departments } from "../mocks/departments";

export const getMaturityLevelType = (id: number): MaturityLevelType => {
  switch (id) {
    case 1:
      return 'All';
    case 2:
      return 'One';
    case 3:
      return 'Two';
    case 4:
      return 'Three';
    default:
      return 'Four';
  }
}

export const getIdString = (str: string) => {
  const match = str.match(/\/(?:\d+_\.\d+\/){2}(\d+)_\.\d+/);

  if (match) return Number(match[1]);
};

const getIdColumn = (str: string) => {
  const match = str.match(/\/(\d+)_\.000/);

  if (match) return Number(match[1]);
}

export const getRequirementString = (str: string) => {
  const match = str.match(/\/([^/]+)\/[^/]*$/);

  if (match) return match[1];
}

export const getDateFromObject = (obj: {month: number, year: number}) => {
  const monthIndex = obj.month - 1;

  return new Date(obj.year, monthIndex, 1);
}

export const getUniqueActions = (actions: Array<Record<string, any>>) => {
  if (actions === null) return;

  const allItems = Object.values(actions).flatMap(
    action => action.actionsResponse && action.actionsResponse.items || []
  );

  const uniqueItems = Array.from(
    new Map(
        allItems.map(item =>
            [item.Subdivision.get_lookupValue() + item.DemandVPOColumns.get_lookupValue(), item]
        )
    ).values()
  );

  return uniqueItems;
}

export const replaceTitle = (title: string) => {
  if(!title){
    return title;
  }
  return title
    .trim()
    .replaceAll("/", "_")
    .replaceAll("\\", "")
    .replaceAll('"', "")
    .replaceAll(":", "")
    .replaceAll(">", "")
    .replaceAll("<", "")
    .replaceAll("=", "");
};

export const getProductionDepartments = (departments: Department[]): Department[] => {
  return departments.filter((el) => el.isProduction);
}

export const trasnformDepartments = (data: DepartmentSP[]): Department[] => {
  return data.map((item): Department => ({
    id: item.ID,
    title: item.Title,
    isProduction: item.TypeSubdivision && item.TypeSubdivision.get_lookupValue() === "Производственный",
  }));
}

const getIndexMonth = (month: string) => String(months.indexOf(month) + 1);

const getCompletionDate = (title: string, year: string, month: string, factory: string, data: []) => {
  const currentElement = data.find((item) =>
    item.ColumnTitle === title &&
    item.Year === year &&
    item.Month === getIndexMonth(month) &&
    item.FactoryTitle === factory
  );

  if (!currentElement) return "Не завершено";

  if (currentElement.Status.get_lookupValue() === "В процессе") {
    return "Не завершено";
  } else {
    return currentElement.Modified.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).replace(',', '');
  }
}

const getCompletedAssessment = (title: string, year: string, month: string, factory: string, data: []) => {
  const currentElement = data.find((item) =>
    item.ColumnTitle === title &&
    item.Year === year &&
    item.Month === getIndexMonth(month) &&
    item.FactoryTitle === factory
  );

  if (!currentElement) return "Не завершено";

  if (currentElement.Status.get_lookupValue() === "В процессе") {
    return "Не завершено";
  } else if (currentElement.Status.get_lookupValue() === "Завершено" && !currentElement.AuthorEnd) {
    return "Автозавершение"
  } else {
    return currentElement.AuthorEnd.get_lookupValue();
  }
}

const getAverageValue = (data: {CountYes: string, CountQuestions: string, CountNull: string}[]) => {
  const totalYes = data.reduce((sum, item) => sum + Number(item.CountYes), 0);
  const totalValidQuestions = data.reduce((sum, item) => sum + (Number(item.CountQuestions) - Number(item.CountNull)), 0);

  return totalValidQuestions > 0 ? Math.round((totalYes / totalValidQuestions) * 100) : 0;
}

const getMonthFromString = (str: string) => {
  const date = new Date(str);
  return date.getUTCMonth() + 1;
}

const isCloseActions = (element) => {
  return element.Status.get_lookupValue() === 'Отменено' || element.Status.get_lookupValue() === 'Закрыто' ? true : false;
}

const isOpenActions = (element) => {
  return element.Status.get_lookupValue() === 'Не начато' || element.Status.get_lookupValue() === 'В процессе' || element.Status.get_lookupValue() === 'Просрочено' ? true : false;
}

const getIdAction = (str: string) => {
  const match = str.match(/#VPO_(\d+)/);

  if (match) {
      return +match[1];
  }
}

const getDirectoryId = (str: string) => {
  const matches = str.match(/\/(\d+)_/g);

  if (matches) {
      const numbers = matches.map((m) => m.match(/(\d+)/)[0]);

      return Number(numbers[1]);
  }
}

const isNotAvailableAnswer = (value, arr) => {
  return !arr.some((subdivision) => subdivision.get_lookupValue() === value);
}

const getActions = (actions, inconsistencies, tasks) => {
  const inconsistencyMap = new Map(inconsistencies.map(inc => [inc.ID, inc.IdVPOactions]));
  const actionMap = new Map(actions.map(action => [action.ID, action]));

  const result = tasks.map(task => {
    const actionId = inconsistencyMap.get(getIdAction(task.MImportantHashTagOld));
    const action = actionId ? actionMap.get(actionId) : null;

    return action ? {
        Description: task.MImportantHashTagOld,
        Created: task.Created,
        Status: task.Status,
        Subdivision: action.Subdivision,
        ColumnTitle: action.ColumnTitle,
        FactoryTitle: action.FactoryTitle
    } : null;
  }).filter(item => item !== null);

  return result;
}

const getCumulativeValue = (arr: []) => {
  let sum = 0;

  const result = arr.map((el) => {
    sum += el.value;
    return { ...el, value: sum };
  });

  return result;
}

const getSortMonths = (monthsArr: []) => {
  return monthsArr.sort((a, b) => months.indexOf(a) - months.indexOf(b));
}

export const buildDataMain = (response: ResponseDataMain, month: number, year: number) => {
  const departments = response.departments;
  const columnBlocks = response.columnBlocks;
  const statusQuestionnaire = response.statusQuestionnaire.length > 0
  ? {
      id: response.statusQuestionnaire?.[0].id,
      title: response.statusQuestionnaire?.[0].statusQuestionnaire
    }
  : {
      id: null,
      title: 'Не начато'
    }
  ;
  const actions = response.actions || [];

  // Создаем индексы для быстрого поиска ответов
  const answerMap = new Map<string, { answerId: number | null; answer: string; isEdit?: boolean; isAudit?: boolean; modified: string }>();

  for (const answer of response.answers) {
    const key = `${answer.Requirement?.id}_${answer.Subdivision?.id}`;
    const existing = answerMap.get(key);

    if (!existing || new Date(answer.Modified) > new Date(existing.modified)) {
      answerMap.set(key, {
        answerId: answer.id ?? null,
        answer: answer.Condition,
        isEdit: answer.isEdit ?? false,
        isAudit: answer.isAudit ?? false,
        modified: answer.Modified,
      });
    }
  }

  const actionsMap = new Map<string, number[]>();

  for (const action of actions) {
    const key = `${action.requirement?.id}_${action.department?.id}`;
    const existing = actionsMap.get(key) || [];
    actionsMap.set(key, [...existing, action.id]);
  }

  const filteredBlocks = filterByRange(columnBlocks, month, year);

  const data = filteredBlocks.map(block => {
    const groupMap = new Map();

    const filteredRequirements = filterByRange(block.requirements, month, year);

    for (const requirement of filteredRequirements) {
      const processTitle = requirement.process?.title;
      const processContentTitle = requirement.process?.BlockContentTitle;
      const processContent = requirement.process?.BlockContent;
      const processLinks = requirement.process?.BlockContentMedia?.links;
      const processFiles = requirement.process?.BlockContentMedia?.files;

      const info = processContentTitle || processContent ? {title: processContentTitle, text: processContent, links: processLinks, files: processFiles} : undefined;

      if (!groupMap.has(processTitle)) {
        groupMap.set(
          processTitle,
          {
            requirements: [],
            information: info
          }
        );
      }

      const answers = departments.map((department) => {
        const key = `${requirement.id}_${department.id}`;
        const answerObj = answerMap.get(key);

        const requirementActions = actionsMap.get(key);

        return {
          answerId: answerObj?.answerId ?? null,
          departmentId: department.id,
          department: department.title,
          answer: answerObj?.answer || 'Нет',
          isEdit: answerObj?.isEdit ?? false,
          isAudit: answerObj?.isAudit ?? false,
          actions: requirementActions ? requirementActions.map(String) : null,
        };
      });

      groupMap.get(processTitle).requirements.push({
        requirementId: requirement.id,
        title: requirement.title,
        indicator: requirement.indicator,
        maturityLevel: requirement.maturityLevel === 'Все' || !requirement.maturityLevel ? 0 :  requirement.maturityLevel,
        documentation: requirement.documentation ?? requirement.documentation,
        blockId: block.id,
        blockTitle: block.title,
        processId: requirement.process?.id,
        processTitle: requirement.process?.title,
        answers,
        information: requirement.Information
          ? {
            title: 'Дополнительная информация по внедрению требования',
            text: requirement.Information,
            links: requirement.InformationMedia ? requirement.InformationMedia.links : null,
            files: requirement.InformationMedia ? requirement.InformationMedia.files : null,
          }
          : undefined,
        connectionColumns: requirement.RelationContent
          ? {
            title: 'Связь с другими колоннами',
            text: requirement.RelationContent,
            links: requirement.RelationContentMedia ? requirement.RelationContentMedia.links : null,
            files: requirement.RelationContentMedia ? requirement.RelationContentMedia.files : null,
          }
          : undefined,
      });
    }

    const groupRequirements = Array.from(groupMap.entries()).map(([title, { requirements, information }]) => ({
      title,
      information,
      requirements,
    }));

    return {
      title: block.title,
      groupRequirements,
    };
  });

  return {
    data,
    dataHead: [...departments],
    statusQuestionnaire
  };
};

export const buildReportFactory = (response: ResponseReportFactory, group: number) => {
  const filteredDepartments =
    response.params.filters.subdivisionFactory.selected.length > 0
      ? response.data.departments.filter((item) =>
          response.params.filters.subdivisionFactory.selected.includes(item.title),
        )
      : response.data.departments;
  // Создаем индексы для быстрого поиска ответов

  const answerMap = new Map<string, { answer: string; modified: string }>();

  for (const answer of response.data.answers) {
    const key = [
      answer.Year,
      answer.Month,
      answer.Factory?.id,
      answer.Subdivision?.id,
      answer.Requirement?.id,
    ].join('|');

    const existing = answerMap.get(key);

    if (!existing || new Date(answer.Modified) > new Date(existing.modified)) {
      answerMap.set(key, {
        answer: answer.Condition,
        modified: answer.Modified,
      });
    }
  }

  const data = [];

  for (const year of response.params.year) {
    for (const month of response.params.month) {
      for (const factory of response.data.factories) {
        for (const column of response.data.columns) {
          const columnRequirements = [];

          const filteredBlocks = filterByRange(column.blocks, month, year);

          const currentBlocks =
            response.params.filters.columnBlockFactory.selected.length > 0
              ? filteredBlocks.filter((item) =>
                  response.params.filters.columnBlockFactory.selected.includes(item.title),
                )
              : filteredBlocks;

          for (const block of currentBlocks) {
            const filteredRequirements = filterByRange(block.requirements, month, year);

            const groupMap = new Map();

            for (const requirement of filteredRequirements) {
              const processTitle = requirement.process?.title;

              if (!groupMap.has(processTitle)) {
                groupMap.set(processTitle, []);
              }

              const answers = filteredDepartments.map((department) => {
                const key = [
                  year,
                  month,
                  factory.id,
                  department.id,
                  requirement.id,
                ].join('|');

                const answerObj = answerMap.get(key);

                const arrayDepartments = group === 1
                  ? column.departments
                  : column.departmentsVOP;

                return {
                  department: department.title,
                  answer: !arrayDepartments.some((column) => column.title === department.title) ? 'N/A' : answerObj?.answer ? answerObj.answer : 'Нет',
                };
              });

              groupMap.get(processTitle).push({
                title: requirement.title,
                indicator: requirement.indicator,
                maturityLevel: requirement.maturityLevel,
                answers,
              });
            }

            const groupRequirements = Array.from(groupMap, ([title, requirements]) => ({
              title,
              requirements,
            }));

            columnRequirements.push({
              title: block.title,
              groupRequirements,
            });
          }

          const currentQuestionnaire = findQuestionnaire(response.data.statusQuestionnaires, year, month, factory.title, column.title);

          if (columnRequirements.length > 0) {
            data.push({
              id: column.id,
              title: column.title,
              year: String(year),
              month: months[month - 1],
              factory: factory.code,
              columnRequirements,
              completedAssessment: currentQuestionnaire.statusQuestionnaire === 'Не начато' || currentQuestionnaire.statusQuestionnaire === 'В процессе' ? 'Не завершено' : currentQuestionnaire.AuthorEnd,
              completionDate: currentQuestionnaire.statusQuestionnaire === 'Не начато' || currentQuestionnaire.statusQuestionnaire === 'В процессе' ? 'Не завершено' : currentQuestionnaire.completionDate,
            });
          }
        }
      }
    }
  }

  return {
    data,
    departments: response.params.filters.subdivisionFactory.selected.length > 0
      ? response.data.departments
          .filter((item) =>
            response.params.filters.subdivisionFactory.selected.includes(item.title),
          )
      : response.data.departments
  }
};

export const buildDataSummary = (response: ResponseSummary, month: number, year: number, group: number) => {
  const departments = response.departments;

  console.log('buildDataSummary', group)

  // Создаем индексы для быстрого поиска ответов
  const answerMap = new Map<string, { answer: string; modified: string }>();

  for (const answer of response.answers) {
    const key = `${answer.Requirement?.id}_${answer.Subdivision?.id}`;
    const existing = answerMap.get(key);

    if (!existing || new Date(answer.Modified) > new Date(existing.modified)) {
      answerMap.set(key, {
        answer: answer.Condition,
        modified: answer.Modified,
      });
    }
  }

  const data = response.columns.map((column) => ({
    title: column.title,
    columnRequirements: filterByRange(column.blocks, month, year).map((block) => {
      const filteredRequirements = filterByRange(block.requirements, month, year);

      const groupMap = new Map();

      for (const requirement of filteredRequirements) {
        const processTitle = requirement.process?.title;

        if (!groupMap.has(processTitle)) {
          groupMap.set(processTitle, []);
        }

        const answers = departments.map((department) => {
          const key = `${requirement.id}_${department.id}`;
          const answerObj = answerMap.get(key);

          const arrayDepartments = group === 1
            ? column.departments
            : column.departmentsVOP;

          return {
            department: department.title,
            answer: !arrayDepartments.some((column) => column.title === department.title) ? 'N/A' : answerObj?.answer ? answerObj.answer : 'Нет',
            noRender: !arrayDepartments.some((column) => column.title === department.title) ? true : false
          };
        });

        groupMap.get(processTitle).push({
          title: requirement.title,
          process: requirement.process,
          indicator: requirement.indicator,
          maturityLevel: requirement.maturityLevel,
          answers,
        });
      }

      const groupRequirements = Array.from(groupMap, ([title, requirements]) => ({
        title,
        requirements,
      }));

      return {
        title: block.title,
        groupRequirements,
      };
    }),
  }));


  return {
    data,
    dataHead: [...departments],
  };
};


export const buildDataActions = (data: ResponseActions) => {
  const months = [...Array(12).keys()].map((i) => i + 1);

  const averageValue = months.map((month) => {
    const filteredData = data.response_region.items.filter((item) => Number(item.Month) === month);

    let currentData;

    if (data.filters.department.selected.length > 0 && data.filters.factoryReport.selected.length > 0) {
      currentData = filteredData
        .filter((item) => data.filters.department.selected.includes(item.SubdivisionTitle))
        .filter((item) => data.filters.factoryReport.selected.includes(item.FactoryTitle))
    } else if (data.filters.department.selected.length > 0) {
      currentData = filteredData.filter((item) => data.filters.department.selected.includes(item.SubdivisionTitle))
    } else if (data.filters.factoryReport.selected.length > 0) {
      currentData = filteredData.filter((item) => data.filters.factoryReport.selected.includes(item.FactoryTitle))
    } else {
      currentData = filteredData
    }

    return {
      id: month,
      value: currentData.length > 0 ? getAverageValue(currentData) : 0,
    };
  });

  const openActions = months.map((month) => {
    if (data.response_tasks === null) return { id: month, value: 0 };

    const filteredData = getActions(
      data.response_actions.items,
      data.response_inconsistencies,
      data.response_tasks.items,
    ).filter(
      (item) => getMonthFromString(item.Created) === month && isOpenActions(item),
    );

    let currentData;

    if (data.filters.department.selected.length > 0 && data.filters.factoryReport.selected.length > 0) {
      currentData = filteredData
        .filter((item) => data.filters.department.selected.includes(item.Subdivision.get_lookupValue()))
        .filter((item) => data.filters.factoryReport.selected.includes(item.FactoryTitle))
    } else if (data.filters.department.selected.length > 0) {
      currentData = filteredData.filter((item) => data.filters.department.selected.includes(item.Subdivision.get_lookupValue()))
    } else if (data.filters.factoryReport.selected.length > 0) {
      currentData = filteredData.filter((item) => data.filters.factoryReport.selected.includes(item.FactoryTitle))
    } else {
      currentData = filteredData
    }

    return {
      id: month,
      value: currentData.length > 0 ? currentData.length : 0,
    };
  });

  const closeActions = months.map((month) => {
    if (data.response_tasks === null) return { id: month, value: 0 };

    const filteredData = getActions(
      data.response_actions.items,
      data.response_inconsistencies,
      data.response_tasks.items,
    ).filter(
      (item) => getMonthFromString(item.Created) === month && isCloseActions(item)
    );

    let currentData;

    if (data.filters.department.selected.length > 0 && data.filters.factoryReport.selected.length > 0) {
      currentData = filteredData
        .filter((item) => data.filters.department.selected.includes(item.Subdivision.get_lookupValue()))
        .filter((item) => data.filters.factoryReport.selected.includes(item.FactoryTitle))
    } else if (data.filters.department.selected.length > 0) {
      currentData = filteredData.filter((item) => data.filters.department.selected.includes(item.Subdivision.get_lookupValue()))
    } else if (data.filters.factoryReport.selected.length > 0) {
      currentData = filteredData.filter((item) => data.filters.factoryReport.selected.includes(item.FactoryTitle))
    } else {
      currentData = filteredData
    }

    return {
      id: month,
      value: currentData.length > 0 ? currentData.length : 0,
    };
  });

  return {
    data: averageValue,
    openActions: getCumulativeValue(openActions),
    closeActions: getCumulativeValue(closeActions)
  }
};

//! Обработка данных из vpo-report-actions, не удалять
export const buildDataReportActions = (data: any) => {
  if (!data || !data.data || !data.openActions || !data.closeActions) {
    return {
      data: Array(12).fill(0).map((_, i) => ({ value: 0, label: String(i + 1) })),
      openActions: Array(12).fill(0).map((_, i) => ({ value: 0, label: String(i + 1) })),
      closeActions: Array(12).fill(0).map((_, i) => ({ value: 0, label: String(i + 1) })),
    };
  }

  return {
    data: data.data,
    openActions: data.openActions,
    closeActions: data.closeActions,
  };
};
